import { WORKSPACE_FRAME_PADDING } from '@components/v2/constants';
import { useResizablePanelLayout } from '@components/v2/layout/resizablePanel/hooks/useResizablePanelLayout';
import { useResizablePanelStyles } from '@components/v2/layout/resizablePanel/resizablePanel.styles';
import ResizeSeparator from '@components/v2/ui/ResizeSeparator/ResizeSeparator';
import { useTheme } from '@mui/material';
import { ReactNode } from 'react';
import { Group, Panel, PanelSize } from 'react-resizable-panels';
import LangEnum from '../../../../../lang/LangEnum';
import {
  WORKSPACE_ASIDE_LAYOUT_STORAGE_ID,
  WORKSPACE_ASIDE_MAX_SIZE_RATIO,
  WORKSPACE_ASIDE_PANEL_ID,
  WORKSPACE_CONTENT_PANEL_ID,
} from '../appTemplate.consts';
import { WorkspaceAsideConfig } from '../appTemplate.types';
import { getWorkspaceContentMinWidth } from '../appTemplate.utils';

interface WorkspaceAsideLayoutProps {
  aside: WorkspaceAsideConfig;
  children: ReactNode;
}

const WORKSPACE_ASIDE_MAX_SIZE = `${WORKSPACE_ASIDE_MAX_SIZE_RATIO * 100}%`;
const MEASURED_WIDTH_TOLERANCE = 1;

export const WorkspaceAsideLayout = ({ aside, children }: WorkspaceAsideLayoutProps) => {
  const theme = useTheme();
  const { defaultLayout, onLayoutChanged } = useResizablePanelLayout(
    `${WORKSPACE_ASIDE_LAYOUT_STORAGE_ID}:${aside.layoutId}`,
  );
  const { classes, cx } = useResizablePanelStyles({
    separatorOffset: `calc(${theme.spacing(WORKSPACE_FRAME_PADDING)} / -2)`,
  });

  const handleResize = (size: PanelSize) => {
    if (size.asPercentage <= 0 || size.inPixels < aside.minWidth - MEASURED_WIDTH_TOLERANCE) {
      return;
    }

    aside.onSizeChange?.({
      width: Math.max(size.inPixels, aside.minWidth),
      groupWidth: (size.inPixels * 100) / size.asPercentage,
    });
  };

  return (
    <Group orientation="horizontal" defaultLayout={defaultLayout} onLayoutChanged={onLayoutChanged}>
      <Panel
        id={WORKSPACE_CONTENT_PANEL_ID}
        minSize={aside.isOpen ? getWorkspaceContentMinWidth(theme) : undefined}
        style={{ display: 'flex', overflow: 'hidden' }}
      >
        {children}
      </Panel>
      <ResizeSeparator
        className={cx(classes.separator, !aside.isOpen && classes.separatorClosed)}
        disabled={!aside.isOpen}
        label={aside.resizeLabel}
        hint={LangEnum.GLOBAL_DETAILS_PANEL_RESIZE_HINT}
      />
      <Panel
        id={WORKSPACE_ASIDE_PANEL_ID}
        defaultSize={aside.minWidth}
        minSize={aside.minWidth}
        maxSize={WORKSPACE_ASIDE_MAX_SIZE}
        groupResizeBehavior="preserve-pixel-size"
        onResize={handleResize}
        style={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}
      >
        {aside.panel}
      </Panel>
    </Group>
  );
};
